import { useDebugValue, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery, useMutation } from '@app/QueryClient';
import { useQueryString } from '@app/useQueryString';
import { usePagination } from '@app/hooks/usePagination';
import { generatedKeys } from '@app/data';
import {
	createGift,
	deleteGift,
	updateGift,
	giftFriendCategories,
	listGifts,
} from '@services/GiftFriend';

const { giftFriend } = generatedKeys;

export function useGifts(opt: any) {
	const [giftsList, setGiftsList] = useState([]);
	const [queryState] = useQueryString();

	const formatGift = opt?.formatGift || ((d: any) => d);

	const q = useQuery({
		queryFn: listGifts,
		staleTime: Infinity,
		queryKey: giftFriend.gifts({
			paginate: opt?.paginate,
			page: queryState.page,
		}),
	});

	const { data: giftsData } = q;

	const pagination = usePagination(giftsData || {});

	useEffect(() => {
		if (giftsData) {
			const formattedData = giftsData?.data?.map(formatGift);
			setGiftsList(formattedData);
		}
	}, [giftsData, setGiftsList]);

	useDebugValue(q);

	return { ...q, ...pagination, giftsList };
}

export function useAddGift(opt: any) {
	const { t } = useTranslation('giftfriend');

	const mutation = useMutation(createGift, {
		...opt,
		moduleName: t('gift'),
		operation: 'add',
	});

	const { mutate } = mutation;

	return {
		...mutation,
		doAddGift: mutate,
	};
}

export function useEditGift(opt: any) {
	const { t } = useTranslation('giftfriend');

	const mutation = useMutation(updateGift, {
		...opt,
		moduleName: t('gift'),
		operation: 'update',
	});

	const { mutate } = mutation;

	return {
		...mutation,
		doEditGift: mutate,
	};
}

export function useDeleteGift(opt: any) {
	const { t } = useTranslation('giftfriend');

	const mutation = useMutation(deleteGift, {
		...opt,
		moduleName: t('gift'),
		operation: 'delete',
	});

	const { mutateAsync } = mutation;

	return {
		...mutation,
		doDeleteGift: mutateAsync,
	};
}

export function useGiftFriendCategories(opt?: any) {
	const [categoriesList, setCategoriesList] = useState([]);

	const formatCategory = opt?.formatCategory || ((d: any) => d);

	const q = useQuery({
		queryFn: giftFriendCategories,
		staleTime: Infinity,
		queryKey: giftFriend.categories({}),
	});

	const { data: categoriesData } = q;

	useEffect(() => {
		if (categoriesData) {
			const formattedData = categoriesData?.categories?.map(formatCategory);
			setCategoriesList(formattedData);
		}
	}, [categoriesData, setCategoriesList]);

	useDebugValue(q);

	return { ...q, categoriesList };
}
